import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, InputAdornment, Slider, Switch, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';

/**
 * "Footer width" card — controls the footer's container size and background
 * stretching. Reusable for any block that needs the same width/bg settings.
 *
 * value shape:
 *   { maxContentWidth: number,         // px
 *     backgroundFullWidth: bool        // stretch bg to viewport edges
 *   }
 */
const FooterWidthCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    return (
        <ConfigCard
            title={tr('cfg_footer_width_title', 'Footer width')}
            description={tr('cfg_footer_width_desc', 'Customise the footer. The bg can stretch to edge while the content stays centered.')}
            theme={theme}
        >
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_footer_width_max', 'Maximum content width')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                        size="small"
                        min={400}
                        max={2400}
                        step={20}
                        value={Number(get('maxContentWidth', 1500)) || 1500}
                        onChange={(_e, v) => set('maxContentWidth')(v)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}px`}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        value={get('maxContentWidth', 1500) ?? 1500}
                        onChange={(e) => set('maxContentWidth')(Math.max(0, parseInt(e.target.value) || 0))}
                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        sx={{ width: 110 }}
                    />
                </Box>
            </Box>

            <FormControlLabel
                control={
                    <Switch color="success"
                        size="small"
                        checked={!!get('backgroundFullWidth', true)}
                        onChange={(e) => set('backgroundFullWidth')(e.target.checked)}
                    />
                }
                label={<Typography variant="body2">{tr('cfg_footer_width_bg_full', 'Background stretches to viewport edges')}</Typography>}
            />
        </ConfigCard>
    );
};

FooterWidthCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default FooterWidthCard;
